import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQuantity, removeItem, subtotal } = useCart();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ lookup_key: i.lookup_key, size: i.size, quantity: i.quantity })),
        origin_url: window.location.origin,
        email,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-overlay"
            className="fixed inset-0 z-[60] bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed top-0 right-0 bottom-0 z-[70] w-full max-w-md bg-white text-black flex flex-col border-l border-black"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-between px-8 py-6 border-b border-black/10">
              <span className="text-xs tracking-[0.3em] uppercase font-semibold">Your Cart</span>
              <button
                onClick={() => setOpen(false)}
                data-testid="cart-close-button"
                className="hover:rotate-90 transition-transform duration-300"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-8">
              {items.length === 0 && (
                <p data-testid="cart-empty-message" className="font-serif italic text-xl text-black/50 pt-10">
                  Nothing here yet. The gallery awaits.
                </p>
              )}
              {items.map((item) => (
                <div key={item.key} data-testid={`cart-item-${item.key}`} className="flex gap-5">
                  <div className="w-20 h-24 bg-[#EBEBEB] overflow-hidden shrink-0">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-3">
                      <p className="text-[11px] tracking-[0.15em] uppercase font-semibold leading-snug">{item.name}</p>
                      <button
                        onClick={() => removeItem(item.key)}
                        data-testid={`cart-remove-${item.key}`}
                        className="text-black/40 hover:text-black transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-black/50 mt-1">Size {item.size}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-black/20">
                        <button
                          onClick={() => updateQuantity(item.key, -1)}
                          data-testid={`cart-qty-minus-${item.key}`}
                          className="px-2.5 py-1 hover:bg-black hover:text-white transition-colors"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="px-3 text-xs">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.key, 1)}
                          data-testid={`cart-qty-plus-${item.key}`}
                          className="px-2.5 py-1 hover:bg-black hover:text-white transition-colors"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <span className="text-sm font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-black px-8 py-6 space-y-4">
              <div className="flex justify-between text-sm">
                <span className="tracking-[0.2em] uppercase text-black/60">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-semibold">${subtotal.toFixed(2)}</span>
              </div>
              <p className="text-[10px] tracking-[0.15em] uppercase text-black/40">
                Flat $2.50 shipping — tax calculated at checkout
              </p>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="EMAIL — FOR YOUR RECEIPT & DROP UPDATES"
                data-testid="cart-email-input"
                className="w-full bg-transparent border-b border-black/25 py-2.5 text-[11px] tracking-[0.2em] uppercase placeholder:text-black/35 focus:outline-none focus:border-black transition-colors"
              />
              <button
                onClick={checkout}
                disabled={loading || items.length === 0}
                data-testid="cart-checkout-button"
                className="w-full bg-black text-white py-4 text-xs tracking-[0.3em] uppercase font-semibold flex items-center justify-center gap-3 hover:bg-white hover:text-black border border-black transition-colors duration-300 disabled:opacity-40 disabled:pointer-events-none"
              >
                {loading ? "Redirecting…" : "Checkout"} <ArrowRight size={14} />
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
